import { Grid, Grow, IconButton, Slider, Stack } from "@mui/material";
import { HorizontalOnlyContainer } from "../../../component/HorizontalOnlyContainer";
import { GameData, TeamTurns } from "../../../../domain/model/Game";
import {
  Answer,
  Question,
  QuestionType,
} from "../../../../domain/model/Question";
import {
  FullscreenExitRounded,
  FullscreenRounded,
  PauseRounded,
  PlayArrowRounded,
  RestoreRounded,
} from "@mui/icons-material";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import { CustomTextView } from "../../../component/CustomTextView";
import { useEffect, useRef, useState } from "react";
import { QuestionViewModel } from "./QuestionViewModel";
import { CustomCardView } from "../../../component/CustomCardView";
import { formattedTime, getDifficultyInfo } from "../../../../core/utils/Utils";
import { CustomButtonView } from "../../../component/CustomButtonView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { QuestionMobileView } from "./QuestionMobileView";
import { useTranslation } from "react-i18next";
import { QuestionAnswerDetailsView } from "./QuestionAnswerDetailsView";
import {
  Coordinate,
  RandomCharacterSpawner,
} from "../../../component/RandomCharacterSpawner";
import ReactPlayer from "react-player";

export function QuestionView({
  gameDataInput,
  questionInput,
  answerInput,
  onClosePopover,
}: {
  isEditing?: boolean;
  gameDataInput: GameData;
  onClosePopover: ({ who }: { who?: string }) => void;
  questionInput: Question | undefined;
  answerInput: Answer | undefined;
}) {
  const viewModel = QuestionViewModel();

  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";

  useEffect(() => {
    if (!questionInput || !answerInput) return;
    viewModel.init(gameDataInput, questionInput, answerInput);

    setChecked(true);
  }, []);

  const gameData = viewModel.gameData;
  const question = viewModel.question;
  const answer = viewModel.answer;

  const { width } = useWindowDimensions();
  const isMobile = width < 1200;

  const [checked, setChecked] = useState(false);

  const countdownTimerVisible = !viewModel.bothTeamAnswered;

  const WHO_TO_ANSWER_ALLOWED_SPOTS: Coordinate[] = [
    { top: "5%", left: "15%" }, // Spot 1: Top Left
    { top: "5%", right: "15%" }, // Spot 2: Top Right
    { top: "60%", right: "32.5%" }, // Spot 3: Bottom Center
  ];

  const BELOW_COUNTER_ALLOWED_SPOTS: Coordinate[] = [
    { bottom: "5%", right: "18%" },
  ];

  return (
    <HorizontalOnlyContainer>
      {!gameData || !question || !answer ? (
        "ERROR"
      ) : (
        <Stack
          width={isMobile ? "95vw" : "98vw"}
          height={isMobile ? "100vh" : "96vh"}
          alignItems={"center"}
          justifyContent={"center"}
          p={isMobile ? 1 : 2}
        >
          {(
            question?.type == null
              ? false
              : question?.type != QuestionType.NORMAL &&
                !viewModel.videoFinished
          ) ? (
            <QuestionVideoOnlyView
              question={question}
              onVideoEnded={() => viewModel.setVideoFinished(true)}
              playCount={question.type == QuestionType.TYPE_3 ? 2 : 1}
            />
          ) : (
            <>
              {isMobile ? (
                <QuestionMobileView
                  onClosePopover={onClosePopover}
                  viewModel={viewModel}
                />
              ) : (
                <Stack width={"100%"} height={"100%"} gap={2}>
                  <Stack height={"100%"} width={"100%"}>
                    <Grid container height={"100%"} width={"100%"} spacing={0}>
                      <Grid
                        size={3}
                        sx={{ pl: isRtl ? 2 : 0, pr: isRtl ? 0 : 2 }}
                      >
                        <Stack
                          width={"100%"}
                          height={"100%"}
                          alignItems={"center"}
                          gap={2}
                        >
                          <CustomCardView
                            width={"100%"}
                            height={"100%"}
                            children={
                              <Grow
                                in={checked}
                                {...(checked ? { timeout: 1000 } : {})}
                                mountOnEnter
                                unmountOnExit
                              >
                                <Stack
                                  width={"100%"}
                                  height={"100%"}
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                  gap={4}
                                >
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    gap={4}
                                  >
                                    <CustomTextView
                                      text={gameData.firstTeamName}
                                      variant="h3"
                                      fontWeight={
                                        gameData.currentTeamTurn ==
                                        TeamTurns.teamA
                                          ? "bold"
                                          : "normal"
                                      }
                                      textColor={COLOR_BLACK}
                                    />

                                    <CustomTextView
                                      text={gameData?.firstTeamScore?.toString()}
                                      fontWeight={
                                        gameData.currentTeamTurn ==
                                        TeamTurns.teamA
                                          ? "bold"
                                          : "normal"
                                      }
                                      variant="h3"
                                      textColor={COLOR_BLACK}
                                    />
                                  </Stack>
                                </Stack>
                              </Grow>
                            }
                          />
                          <CustomCardView
                            width={"100%"}
                            height={"100%"}
                            children={
                              <Grow
                                in={checked}
                                {...(checked ? { timeout: 1000 } : {})}
                                mountOnEnter
                                unmountOnExit
                              >
                                <Stack
                                  width={"100%"}
                                  height={"100%"}
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                  gap={4}
                                >
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    gap={4}
                                  >
                                    <CustomTextView
                                      text={gameData.secondTeamName}
                                      fontWeight={
                                        gameData.currentTeamTurn ==
                                        TeamTurns.teamB
                                          ? "bold"
                                          : "normal"
                                      }
                                      textColor={COLOR_BLACK}
                                      variant="h3"
                                    />
                                    <CustomTextView
                                      text={gameData?.secondTeamScore?.toString()}
                                      fontWeight={
                                        gameData.currentTeamTurn ==
                                        TeamTurns.teamB
                                          ? "bold"
                                          : "normal"
                                      }
                                      textColor={COLOR_BLACK}
                                      variant="h3"
                                    />
                                  </Stack>
                                </Stack>
                              </Grow>
                            }
                          />
                        </Stack>
                      </Grid>
                      <Grid size={6}>
                        <Stack
                          width={"100%"}
                          height={"100%"}
                          alignItems={"center"}
                          gap={2}
                        >
                          <CustomCardView
                            width={"100%"}
                            height={"85%"}
                            background={COLOR_MAIN}
                            children={
                              <>
                                {viewModel.bothTeamAnswered &&
                                viewModel.showWhoAnswered ? (
                                  <Stack
                                    width={"100%"}
                                    height={"100%"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                    position={"relative"}
                                    gap={1}
                                  >
                                    <Stack
                                      direction={"row"}
                                      width={"100%"}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                      gap={1}
                                    >
                                      <Stack width={"100%"}>
                                        <CustomButtonView
                                          onClick={() => {
                                            onClosePopover({
                                              who: gameData.firstTeamName,
                                            });
                                          }}
                                          props={{ pt: 2, pb: 2 }}
                                          fontSize="h5"
                                          text={gameData.firstTeamName}
                                          backgroundColor={COLOR_WHITE}
                                          textColor={COLOR_MAIN}
                                        />
                                      </Stack>

                                      <Stack width={"100%"}>
                                        <CustomButtonView
                                          fontSize="h5"
                                          props={{ pt: 2, pb: 2 }}
                                          onClick={() => {
                                            onClosePopover({
                                              who: gameData.secondTeamName,
                                            });
                                          }}
                                          text={gameData.secondTeamName}
                                          backgroundColor={COLOR_WHITE}
                                          textColor={COLOR_MAIN}
                                        />
                                      </Stack>
                                    </Stack>

                                    <Stack width={"100%"}>
                                      <CustomButtonView
                                        fontSize="h5"
                                        onClick={() => {
                                          onClosePopover({});
                                        }}
                                        props={{ pt: 2, pb: 2 }}
                                        text={t("no_one_answered")}
                                        backgroundColor={COLOR_WHITE}
                                        textColor={COLOR_MAIN}
                                      />
                                    </Stack>

                                    <RandomCharacterSpawner
                                      allowedLocations={
                                        WHO_TO_ANSWER_ALLOWED_SPOTS
                                      }
                                    />
                                  </Stack>
                                ) : (
                                  <QuestionAnswerDetailsView
                                    question={question}
                                    answer={answer}
                                    showAnswer={viewModel.showAnswer}
                                    isMobile={false}
                                    showAttachment={
                                      question?.type == null
                                        ? true
                                        : question?.type == QuestionType.TYPE_1
                                          ? viewModel.videoFinished
                                            ? true
                                            : false
                                          : question?.type ==
                                            QuestionType.NORMAL
                                    }
                                  />
                                )}
                              </>
                            }
                          />

                          <CustomCardView
                            width={"100%"}
                            height={"15%"}
                            background={COLOR_MAIN}
                            children={
                              <Stack
                                width={"100%"}
                                height={"100%"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                direction={"row"}
                                gap={2}
                              >
                                {!viewModel.bothTeamAnswered ? (
                                  <CustomButtonView
                                    onClick={viewModel.onAnswerButtonClicked}
                                    text={`${
                                      gameData.currentTeamTurn ==
                                      TeamTurns.teamA
                                        ? !viewModel.hasFirstTeamAnswered
                                          ? gameData.firstTeamName
                                          : gameData.secondTeamName
                                        : !viewModel.hasFirstTeamAnswered
                                          ? gameData.secondTeamName
                                          : gameData.firstTeamName
                                    } ${t("answered")}`}
                                    backgroundColor={COLOR_VERY_LIGHT_GRAY}
                                    textColor={COLOR_BLACK}
                                    props={{
                                      pl: 14,
                                      pr: 14,
                                      direction: "ltr",
                                    }}
                                    elevation={12}
                                    fontSize="h5"
                                  />
                                ) : (
                                  <>
                                    {
                                      <CustomButtonView
                                        onClick={() => {
                                          viewModel.setShowWhoAnswered(true);
                                        }}
                                        text={t("who_answered")}
                                        backgroundColor={COLOR_VERY_LIGHT_GRAY}
                                        textColor={COLOR_BLACK}
                                        props={{ pl: 8, pr: 8 }}
                                        elevation={12}
                                        fontSize="h6"
                                      />
                                    }
                                  </>
                                )}

                                <Stack
                                  direction={"row"}
                                  alignItems={"center"}
                                  gap={2}
                                >
                                  {viewModel.bothTeamAnswered && (
                                    <CustomButtonView
                                      onClick={() => {
                                        viewModel.setShowAnswer(
                                          !viewModel.showAnswer,
                                        );

                                        viewModel.setShowWhoAnswered(false);
                                      }}
                                      text={
                                        viewModel.showAnswer
                                          ? t("see_question")
                                          : t("see_answer")
                                      }
                                      backgroundColor={COLOR_VERY_LIGHT_GRAY}
                                      textColor={COLOR_BLACK}
                                      props={{ pl: 8, pr: 8 }}
                                      elevation={12}
                                      fontSize="h6"
                                    />
                                  )}
                                </Stack>
                              </Stack>
                            }
                          />
                        </Stack>
                      </Grid>
                      <Grid
                        size={3}
                        sx={{ pl: isRtl ? 0 : 2, pr: isRtl ? 2 : 0 }}
                      >
                        <Stack
                          width={"100%"}
                          height={"100%"}
                          alignItems={"center"}
                          gap={2}
                        >
                          <CustomCardView
                            width={"100%"}
                            height={"100%"}
                            children={
                              <Grow
                                in={checked}
                                {...(checked ? { timeout: 1000 } : {})}
                                mountOnEnter
                                unmountOnExit
                              >
                                <Stack
                                  direction={"row"}
                                  alignItems={"center"}
                                  height={"100%"}
                                  width={"100%"}
                                  justifyContent={"space-between"}
                                  position={"relative"}
                                  spacing={1}
                                  sx={{ direction: "ltr" }}
                                >
                                  <Stack
                                    width={"100%"}
                                    height={"100%"}
                                    justifyContent={
                                      !countdownTimerVisible
                                        ? "center"
                                        : "start"
                                    }
                                    alignItems={
                                      !countdownTimerVisible ? "center" : "end"
                                    }
                                    gap={2}
                                  >
                                    <CustomTextView
                                      text={question.category?.title}
                                      fontWeight="bold"
                                      textAlign="center"
                                      variant={
                                        !countdownTimerVisible ? "h4" : "h5"
                                      }
                                    />
                                    <CustomTextView
                                      text={
                                        getDifficultyInfo(
                                          question.difficulty ?? "",
                                          t,
                                        ).text
                                      }
                                      fontWeight="bold"
                                      variant={
                                        !countdownTimerVisible ? "h4" : "h5"
                                      }
                                      textAlign="center"
                                      textColor={
                                        getDifficultyInfo(
                                          question.difficulty ?? "",
                                          t,
                                        ).color
                                      }
                                    />
                                  </Stack>

                                  <Stack
                                    position={"absolute"}
                                    width={"100%"}
                                    justifyContent={"center"}
                                    height={"100%"}
                                    zIndex={2}
                                  >
                                    {countdownTimerVisible && (
                                      <Stack width={"100%"} height={"40%"}>
                                        <CountdownTimer
                                          counter={
                                            viewModel.countdownTimerSeconds
                                          }
                                          setCounter={(c) =>
                                            viewModel.setCountdownTimerSeconds(
                                              c,
                                            )
                                          }
                                          setPausedResumed={(is) => {
                                            viewModel.setTimerPausedOrResumed(
                                              is,
                                            );
                                          }}
                                          hasFirstTeamAnswered={
                                            viewModel.hasFirstTeamAnswered
                                          }
                                        />
                                      </Stack>
                                    )}
                                  </Stack>

                                  <RandomCharacterSpawner
                                    allowedLocations={
                                      BELOW_COUNTER_ALLOWED_SPOTS
                                    }
                                  />
                                </Stack>
                              </Grow>
                            }
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}
    </HorizontalOnlyContainer>
  );
}

export function QuestionVideoOnlyView({
  question,
  onVideoEnded,
  playCount = 1, // New prop with default value 1
}: {
  question: any;
  onVideoEnded: () => void;
  playCount?: number;
}) {
  const playerRef = useRef<HTMLVideoElement>(null); // Changed to any for ReactPlayer ref compatibility
  const [playing, setPlaying] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [playedFraction, setPlayedFraction] = useState(0);

  // Track how many times the video has completed
  const [currentLoop, setCurrentLoop] = useState(0);

  const height = isFullScreen ? "100%" : "50vh";
  const width = isFullScreen ? "100%" : "50vw";

  const updateProgress = () => {
    // Note: ReactPlayer refs use internal methods, so we use getInternalPlayer()
    // or access the current progress directly depending on your ReactPlayer version
    if (playerRef.current) {
      const current = playerRef.current.currentTime;

      const total = playerRef.current.duration;

      if (total > 0) {
        setPlayedFraction(current / total);
      }
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      updateProgress();
    }, 50);
    return () => clearInterval(intervalId);
  }, []);

  // Handle the logic for looping vs finishing
  const handleVideoEnded = () => {
    const nextLoopCount = currentLoop + 1;

    if (nextLoopCount < playCount) {
      // Increment the loop counter
      setCurrentLoop(nextLoopCount);
    } else {
      // Reset counter for next time the user clicks play manually
      setCurrentLoop(0);
      onVideoEnded();
    }
  };

  return (
    <Stack
      width={"100%"}
      height={"100%"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Stack
        height={height}
        width={width}
        position={"relative"}
        sx={{ borderRadius: 22 }}
      >
        <ReactPlayer
          ref={playerRef}
          width={"100%"}
          height={"100%"}
          style={{ background: "#000", borderRadius: 22 }}
          controls={false}
          playing={playing}
          src={question.attachment?.downloadUrl} // Changed 'src' to 'url' for ReactPlayer standard
          onEnded={handleVideoEnded}
        />

        {!playing && (
          <Stack
            justifyContent={"center"}
            alignItems={"center"}
            width={"100%"}
            height={"100%"}
            sx={{ position: "absolute", zIndex: 22 }}
          >
            <IconButton
              onClick={() => setPlaying(true)}
              sx={{
                background: "#fff",
                "&:hover": { background: "#f0f0f0" },
              }}
            >
              <PlayArrowRounded sx={{ color: "#000" }} />
            </IconButton>
          </Stack>
        )}

        {/* ... Rest of your Fullscreen and Slider UI remains the same ... */}

        <Stack sx={{ position: "absolute", zIndex: 23, p: 1 }}>
          <IconButton
            onClick={() => setIsFullScreen(!isFullScreen)}
            sx={{ background: "#fff", "&:hover": { background: "#f0f0f0" } }}
          >
            {isFullScreen ? (
              <FullscreenExitRounded sx={{ color: "#000" }} />
            ) : (
              <FullscreenRounded sx={{ color: "#000" }} />
            )}
          </IconButton>
        </Stack>

        {playing && (
          <Stack
            sx={{
              position: "absolute",
              zIndex: 24,
              pb: 1,
              pl: 2,
              pr: 2,
              bgcolor: "rgba(0,0,0,0.2)",
              borderRadius: 22,
            }}
            width={"100%"}
            bottom={0}
          >
            <Slider
              value={playedFraction}
              min={0}
              max={1}
              step={0.001}
              disabled
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export function CountdownTimer({
  counter,
  setCounter,
  setPausedResumed,
  hasFirstTeamAnswered,
  isMobile = false,
}: {
  counter: number;
  setCounter: (count: number) => void;
  setPausedResumed: (paused: boolean) => void;
  hasFirstTeamAnswered: boolean;
  isMobile?: boolean;
}) {
  const [initialCounter, setInitialCounter] = useState(counter);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (counter > initialCounter) {
      setInitialCounter(counter);
    }
  }, [counter]);

  useEffect(() => {
    setPausedResumed(paused);
  }, [paused]);

  useEffect(() => {
    if (hasFirstTeamAnswered) {
      setInitialCounter(counter);
    }
  }, [hasFirstTeamAnswered]);

  useEffect(() => {
    const timer =
      counter > 0 &&
      setInterval(() => {
        if (paused) return;
        setCounter(counter - 1);
      }, 1000);
    if (timer) return () => clearInterval(timer);
  }, [counter, paused]);

  const handlePauseResumeCountdown = () => {
    setPaused(!paused);
  };

  const handleResetCountdown = () => {
    setCounter(initialCounter);
    setPaused(false);
  };

  return (
    <Stack
      gap={2}
      width={"100%"}
      height={"100%"}
      alignItems={"center"}
      justifyContent={"space-between"}
      direction={isMobile ? "row" : "column"}
      sx={{ direction: "ltr" }}
    >
      <Stack
        gap={1}
        width={isMobile ? "30%" : "100%"}
        height={"100%"}
        alignItems={isMobile ? "start" : "center"}
        direction={isMobile ? "column" : "row"}
        justifyContent={"center"}
      >
        <IconButton
          onClick={handleResetCountdown}
          sx={{ width: "4vw", height: "4vw" }}
        >
          <RestoreRounded
            sx={{
              color: isMobile ? COLOR_WHITE : COLOR_BLACK,
              width: "3vw",
              height: "3vw",
            }}
            fontSize={isMobile ? "small" : "large"}
          />
        </IconButton>

        <IconButton
          onClick={handlePauseResumeCountdown}
          sx={{ width: "4vw", height: "4vw" }}
        >
          {paused ? (
            <PlayArrowRounded
              sx={{
                color: isMobile ? COLOR_WHITE : COLOR_BLACK,
                width: "3vw",
                height: "3vw",
              }}
              fontSize={isMobile ? "small" : "large"}
            />
          ) : (
            <PauseRounded
              sx={{
                color: isMobile ? COLOR_WHITE : COLOR_BLACK,
                width: "3vw",
                height: "3vw",
              }}
              fontSize={isMobile ? "small" : "large"}
            />
          )}
        </IconButton>
      </Stack>

      <Stack
        width={isMobile ? "70%" : "100%"}
        height={"100%"}
        alignItems={isMobile ? "end" : "center"}
        justifyContent={"center"}
      >
        <CustomTextView
          text={counter > 0 ? formattedTime(counter) : "-"}
          variant={isMobile ? "h4" : "h3"}
          fontWeight="bold"
          textColor={isMobile ? COLOR_WHITE : COLOR_BLACK}
        />
      </Stack>
    </Stack>
  );
}
